// src/app/context/global-modal-context.tsx

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { getLazyModal, ModalTypes } from '@/components/modal-map'
import { ModalProps } from '@/models/modal'

export interface StoreProps<T extends object = {}> {
  modalType: ModalTypes | null
  modalProps?: T | null
}

interface GlobalModalContextProps<T extends object = {}> {
  showModal: (modalType: ModalTypes, modalProps?: T) => void
  hideModal: () => void
  store: StoreProps<T>
}

const initialStore: StoreProps = {
  modalType: null,
  modalProps: null,
}

const GlobalModalContext = createContext<GlobalModalContextProps>({
  showModal: () => undefined,
  hideModal: () => undefined,
  store: initialStore,
})
GlobalModalContext.displayName = 'GlobalModalContext'

const GlobalModalProvider = <T extends object = {}>({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const [store, setStore] = useState<StoreProps<T>>(initialStore as StoreProps<T>)

  const showModal = useCallback((modalType: ModalTypes, modalProps?: ModalProps<T>) => {
    setStore({ modalType, modalProps: (modalProps as any) ?? null })
  }, [])

  const hideModal = useCallback(() => {
    setStore(initialStore as StoreProps<T>)
  }, [])

  // Close modal on route change
  useEffect(() => {
    hideModal()
  }, [location.key, hideModal])

  const ModalComponent = useMemo(() => {
    if (!store.modalType) return null
    return getLazyModal(store.modalType)
  }, [store.modalType])

  return (
    <GlobalModalContext.Provider value={{ store, showModal, hideModal }}>
      {ModalComponent ? (
        <React.Suspense fallback={null}>
          <ModalComponent modalProps={store.modalProps} onClose={hideModal} />
        </React.Suspense>
      ) : null}

      {children}
    </GlobalModalContext.Provider>
  )
}

const useGlobalModal = <T extends object = {}>(): GlobalModalContextProps<T> => {
  const context = useContext(GlobalModalContext) as GlobalModalContextProps<T>
  if (!context) throw new Error('useGlobalModal must be used within a GlobalModalProvider')
  return context
}

export { GlobalModalProvider, useGlobalModal }
